import {
  ArrowRight,
  Bike,
  Building,
  Calendar,
  Camera,
  Cloud,
  Fish,
  Globe,
  Home,
  Info,
  Mail,
  Map,
  Mountain,
  Music,
  Phone,
  QrCode,
  Route,
  ShoppingBag,
  Star,
  Sunset,
  Trees,
  User,
  Utensils,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FeatureImage } from "@/components/FeatureImage";
import { GuideQrModal } from "@/components/GuideQrModal";
import { HealthCheck } from "@/components/HealthCheck";
import type { NavButton, NavButtonIcon } from "@/components/MunicipalityForm";
import { WebViewModal } from "@/components/WebViewModal";
import { useLanguage } from "@/contexts/LanguageContext";
import { useURLConfig } from "@/hooks/useURLConfig";
import { UI_TRANSLATIONS, type KioskLanguage } from "@/lib/i18n";
import {
  fetchWeatherData,
  type WeatherData,
} from "@/services/weatherService";
import { getWeatherIcon } from "@/utils/weatherIcons";
import totemBackground from "@/assets/totem-background.png";

const ICON_MAP: Record<NavButtonIcon | string, LucideIcon> = {
  Mountain,
  Route,
  User,
  Calendar,
  Map,
  Star,
  Info,
  Home,
  Phone,
  Mail,
  Globe,
  Music,
  Camera,
  ShoppingBag,
  Utensils,
  Trees,
  Building,
  Bike,
  Fish,
  Sunset,
  QrCode,
};

const DATE_LOCALES: Record<KioskLanguage, string> = {
  gl: "gl-ES",
  es: "es-ES",
  en: "en-GB",
};

const HERO_COPY: Record<
  KioskLanguage,
  { heading: string; accent: string; subtitle: string }
> = {
  gl: {
    heading: "Valle del",
    accent: "Guadiato",
    subtitle: "El secreto mejor guardado de Córdoba",
  },
  es: {
    heading: "Valle del",
    accent: "Guadiato",
    subtitle: "El secreto mejor guardado de Córdoba",
  },
  en: {
    heading: "Valle del",
    accent: "Guadiato",
    subtitle: "El secreto mejor guardado de Córdoba",
  },
};

const extractDomain = (url: string): string | null => {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
};

const isUrlAllowed = (url: string, allowedDomains: string[]): boolean => {
  try {
    const domain = new URL(url).hostname.toLowerCase().replace(/^www\./, "");
    return allowedDomains.some(
      (allowed) => domain === allowed || domain.endsWith(`.${allowed}`),
    );
  } catch {
    return false;
  }
};

const Index = () => {
  const config = useURLConfig();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const translations = UI_TRANSLATIONS[language];
  const heroCopy = HERO_COPY[language];
  const [ameliaRunning, setAmeliaRunning] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const secretTapCountRef = useRef(0);
  const secretTapTimerRef = useRef<number | null>(null);
  const [guideState, setGuideState] = useState<{
    isOpen: boolean;
    url: string;
    title?: string;
  }>({
    isOpen: false,
    url: "",
    title: undefined,
  });

  useEffect(() => {
    if (!window.electronAPI) return;
    return window.electronAPI.onAmeliaState((state) => {
      setAmeliaRunning(state === "running");
    });
  }, []);

  useEffect(
    () => () => {
      if (secretTapTimerRef.current !== null)
        window.clearTimeout(secretTapTimerRef.current);
    },
    [],
  );

  const handleSecretTap = () => {
    if (!window.electronAPI) return;

    if (secretTapTimerRef.current !== null)
      window.clearTimeout(secretTapTimerRef.current);

    secretTapCountRef.current += 1;
    if (secretTapCountRef.current >= 5) {
      secretTapCountRef.current = 0;
      secretTapTimerRef.current = null;
      navigate("/auth");
      return;
    }

    // Las cinco pulsaciones deben realizarse en un intervalo máximo de 3 s.
    secretTapTimerRef.current = window.setTimeout(() => {
      secretTapCountRef.current = 0;
      secretTapTimerRef.current = null;
    }, 3_000);
  };

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchWeatherData(
      config.city,
      config.lat,
      config.lon,
      config.apiKey,
      language,
    )
      .then((data) => {
        if (!cancelled) setWeather(data);
      })
      .catch(() => {
        if (!cancelled) setWeather(null);
      });
    return () => {
      cancelled = true;
    };
  }, [config.apiKey, config.city, config.lat, config.lon, language]);

  const launchAmelia = useCallback(() => {
    if (ameliaRunning) return;
    if (!window.electronAPI) {
      console.warn("Amelia solo puede abrirse desde el reproductor del kiosco.");
      return;
    }
    window.electronAPI.launchApp("resources:amelia\\Amelia.exe");
  }, [ameliaRunning]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set("city", "Córdoba");
    params.set("municipality", "Córdoba");
    const hasMunicipalityParams =
      params.has("city") ||
      params.has("municipality") ||
      params.has("lat");

    if (!hasMunicipalityParams) navigate("/auth");
  }, [navigate]);

  const fallbackNavButtons = useMemo<NavButton[]>(
    () => [
      {
        id: "fallback-enjoy",
        label: "Disfruta del territorio",
        icon: "Mountain",
        action: config.enjoyUrl ? "external_url" : "none",
        url: config.enjoyUrl,
      },
      {
        id: "fallback-routes",
        label: "Rutas y caminos",
        icon: "Route",
        action: config.carpetaniaUrl ? "external_url" : "none",
        url: config.carpetaniaUrl,
      },
      {
        id: "fallback-explore",
        label: "Recorre el municipio",
        icon: "User",
        action: "none",
      },
      {
        id: "fallback-events",
        label: "Agenda",
        icon: "Calendar",
        action: "none",
      },
    ],
    [config.carpetaniaUrl, config.enjoyUrl],
  );

  const navButtons =
    config.navButtons?.length > 0 ? config.navButtons : fallbackNavButtons;
  const bottomButtons = config.bottomButtons ?? [];

  const allowedDomains = useMemo(() => {
    const domains = new Set<string>();
    const addDomain = (url?: string) => {
      if (!url) return;
      const domain = extractDomain(url);
      if (domain) domains.add(domain);
    };

    config.heroes.forEach((hero) => addDomain(hero.clickUrl));
    navButtons.forEach((button) => {
      if (button.action === "external_url") addDomain(button.url);
    });
    bottomButtons.forEach((button) => {
      if (button.action === "external_url") addDomain(button.url);
    });

    return Array.from(domains);
  }, [bottomButtons, config.heroes, navButtons]);

  const [webViewState, setWebViewState] = useState<{
    isOpen: boolean;
    url: string;
    title?: string;
  }>({
    isOpen: false,
    url: "",
    title: undefined,
  });

  useEffect(() => {
    const handleCloseWebView = () => {
      setWebViewState({ isOpen: false, url: "", title: undefined });
    };
    window.addEventListener("closeWebView", handleCloseWebView);
    return () => window.removeEventListener("closeWebView", handleCloseWebView);
  }, []);

  const openWebView = useCallback(
    (url: string, title?: string) => {
      if (!isUrlAllowed(url, allowedDomains)) {
        console.log("URL no permitida, ignorando:", url);
        return;
      }
      setWebViewState({ isOpen: true, url, title });
      window.dispatchEvent(new Event("webViewOpen"));
    },
    [allowedDomains],
  );

  const runButtonAction = useCallback(
    (button: NavButton) => {
      if (button.action === "internal_page" && button.page) {
        navigate(`${button.page}${window.location.search}`);
      } else if (button.action === "external_url" && button.url) {
        openWebView(button.url, button.label);
      } else if (button.action === "launch_amelia") {
        launchAmelia();
      } else if (button.action === "guide_qr" && button.url) {
        setGuideState({
          isOpen: true,
          url: button.url,
          title: button.label,
        });
      }
    },
    [launchAmelia, navigate, openWebView],
  );

  const closeWebView = useCallback(() => {
    setWebViewState({ isOpen: false, url: "", title: undefined });
    window.dispatchEvent(new Event("webViewClose"));
  }, []);

  const formattedDate = new Intl.DateTimeFormat(DATE_LOCALES[language], {
    weekday: "long",
    day: "numeric",
    month: "long",
  })
    .format(now)
    .toUpperCase();
  const formattedTime = new Intl.DateTimeFormat(DATE_LOCALES[language], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(now);
  const WeatherIcon = weather ? getWeatherIcon(weather.weatherCode) : Cloud;

  return (
    <div className="totem-page">
      {window.electronAPI && (
        <button
          type="button"
          onClick={handleSecretTap}
          className="absolute top-0 left-0 w-24 h-24 z-50 bg-transparent"
          aria-label="Acceso de mantenimiento"
          tabIndex={-1}
        />
      )}

      <img
        src={totemBackground}
        alt=""
        className="totem-page-bg"
        aria-hidden="true"
      />
      <div className="totem-page-overlay" aria-hidden="true" />
      <HealthCheck />

      <section className="totem-hero">
        <div className="totem-hero-topbar">
          <div className="totem-hero-datetime">
            <div className="totem-hero-date">{formattedDate}</div>
            <div className="totem-hero-time">{formattedTime}</div>
          </div>

          <div className="totem-hero-controls">
            <div className="totem-hero-weather">
              <WeatherIcon className="totem-hero-weather-icon" />
              <div className="totem-hero-weather-info">
                <div className="totem-hero-weather-temp">
                  {weather ? `${weather.temp}°` : "--°"}
                </div>
                <div className="totem-hero-weather-location">
                  {(weather?.location || config.city).toUpperCase()}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="totem-hero-titlewrap">
          <h1 className="totem-hero-title">
            {heroCopy.heading}{" "}
            <span className="hero-subtitle-accent">{heroCopy.accent}</span>
          </h1>
          <p className="totem-hero-subtitle">{heroCopy.subtitle}</p>
        </div>

        <div className="nav-cards-grid">
          {navButtons.map((button, index) => {
            const Icon = ICON_MAP[button.icon] ?? Mountain;
            const label =
              button.action === "launch_amelia" && ameliaRunning
                ? "Abriendo..."
                : button.label;
            return (
              <button
                key={button.id}
                type="button"
                className={`nav-card nav-card-variant-${(index % 4) + 1}`}
                onClick={() => runButtonAction(button)}
              >
                <div className="nav-card-icon-wrap">
                  {button.imageUrl ? (
                    <img
                      src={button.imageUrl}
                      alt=""
                      className="nav-card-custom-image"
                    />
                  ) : (
                    <Icon className="nav-card-icon" strokeWidth={1.75} />
                  )}
                </div>
                <span className="nav-card-title">{label}</span>
                <span className="nav-card-arrow" aria-hidden="true">
                  <ArrowRight strokeWidth={2.25} />
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <main className="totem-main">
        <FeatureImage
          municipalityId={config.municipalityId}
          alt={`Vista de ${config.municipality}`}
        />

        <div className="action-pills-row">
          {bottomButtons.map((button) => {
            const Icon = ICON_MAP[button.icon] ?? Globe;
            const label =
              button.action === "launch_amelia" && ameliaRunning
                ? "Abriendo..."
                : button.page === "/survey"
                  ? translations.surveyTitle
                  : button.label;
            return (
              <button
                key={button.id}
                type="button"
                className="action-pill"
                onClick={() => runButtonAction(button)}
              >
                <span className="action-pill-icon">
                  {button.imageUrl ? (
                    <img src={button.imageUrl} alt="" />
                  ) : (
                    <Icon strokeWidth={1.75} />
                  )}
                </span>
                <span className="action-pill-text">{label}</span>
                <span className="action-pill-arrow" aria-hidden="true">
                  <ArrowRight strokeWidth={2.25} />
                </span>
              </button>
            );
          })}
        </div>
      </main>

      <WebViewModal
        isOpen={webViewState.isOpen}
        onClose={closeWebView}
        url={webViewState.url}
        title={webViewState.title}
        language={language}
        allowedDomains={allowedDomains}
        inactivityPromptEnabled={config.inactivityPromptEnabled}
      />
      <GuideQrModal
        isOpen={guideState.isOpen}
        onClose={() =>
          setGuideState({ isOpen: false, url: "", title: undefined })
        }
        url={guideState.url}
        title={guideState.title}
        language={language}
      />
    </div>
  );
};

export default Index;
